import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Form, message } from "antd";
import {
  buscarAsistenciaID,
  getListaPlantillas,
  getListaRubros,
  getListaSupervisores,
  nuevaEvaluacion,
} from "../shared/evaluacionesApi";
import { hoy } from "../shared/evaluacionesUtils";

export function useNuevaEvaluacion() {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(state => state.userReducer.user);
  const [form] = Form.useForm();
  const asistenciaID = Form.useWatch("asistenciaID", form);
  const usuarioEmpleadoID = Form.useWatch("usuarioEmpleadoID", form);
  const evaluacionID = Form.useWatch("evaluacionID", form);
  const volver = location.state?.origen || "/evaluaciones";
  const [datosIniciales, setDatosIniciales] = useState({
    plantillas: [],
    supervisores: [],
  });
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [errorInicial, setErrorInicial] = useState("");
  const [operadoresServicio, setOperadoresServicio] = useState({ asistenciaID: null, usuarios: [] });
  const [cargandoContexto, setCargandoContexto] = useState(false);
  const [errorContexto, setErrorContexto] = useState("");
  const [captura, setCaptura] = useState({ evaluacionID: null, rubros: [], respuestas: {} });
  const [cargandoRubros, setCargandoRubros] = useState(false);
  const [errorRubros, setErrorRubros] = useState("");
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false);
  const consultaServicioRef = useRef(0);

  const { plantillas, supervisores } = datosIniciales;
  const { rubros, respuestas } = captura;
  const operadores = operadoresServicio.usuarios;
  const operador = operadores.find(item => item.usuarioID === usuarioEmpleadoID);
  const plantilla = plantillas.find(item => item.id === evaluacionID);
  const totalRubros = rubros.length;
  const respondidos = rubros.filter(
    rubro => typeof respuestas[rubro.id].siCumple === "boolean"
  ).length;
  const completo = captura.evaluacionID === evaluacionID &&
    !cargandoRubros && !errorRubros && totalRubros > 0 && respondidos === totalRubros;

  const contextoListo = Boolean(
    operador &&
    operadoresServicio.asistenciaID === asistenciaID?.trim() &&
    !cargandoContexto &&
    !errorContexto
  );
  const formularioListo = contextoListo && Boolean(plantilla);

  const cambiarServicio = () => {
    consultaServicioRef.current += 1;
    setCargandoContexto(false);
    setOperadoresServicio({ asistenciaID: null, usuarios: [] });
    form.resetFields(["usuarioEmpleadoID", "evaluacionID"]);
  };

  const cambiarOperador = () => {
    form.resetFields([
      "evaluacionID", "usuarioSupervisorID", "etiquetaLlamada", "duracion",
      "tipoMonitoreo", "fechaEvaluacion", "puntosAMejorar", "comentarios",
    ]);
  };

  useEffect(() => {
    let vigente = true;
    Promise.all([
      getListaPlantillas(),
      getListaSupervisores(),
    ])
      .then(([plantillas, supervisores]) => {
        if (!vigente) return;
        setDatosIniciales({
          plantillas,
          supervisores,
        });
      })
      .catch(error => {
        if (vigente) setErrorInicial(error.message);
      })
      .finally(() => {
        if (vigente) setCargandoInicial(false);
      });

    return () => {
      vigente = false;
    };
  }, []);

  useEffect(() => {
    setOperadoresServicio({ asistenciaID: null, usuarios: [] });
    setErrorContexto("");
    setCaptura({ evaluacionID: null, rubros: [], respuestas: {} });
    setErrorRubros("");
    form.resetFields([
      "usuarioEmpleadoID",
      "evaluacionID",
      "usuarioSupervisorID",
      "etiquetaLlamada",
      "duracion",
      "tipoMonitoreo",
      "fechaEvaluacion",
      "puntosAMejorar",
      "comentarios",
    ]);
    setCargandoContexto(false);
  }, [asistenciaID, form]);

  useEffect(() => () => {
    consultaServicioRef.current += 1;
  }, []);

  const buscarServicio = () => {
    if (cargandoContexto || cargandoInicial || errorInicial || guardandoRef.current) return;
    const servicio = form.getFieldValue("asistenciaID")?.trim();
    if (!servicio) {
      form.setFields([{ name: "asistenciaID", errors: ["Escribe un servicio"] }]);
      return;
    }
    const consulta = ++consultaServicioRef.current;
    setErrorContexto("");
    setCargandoContexto(true);
    buscarAsistenciaID(servicio)
      .then(data => {
        if (consulta !== consultaServicioRef.current) return;
        if (data.some(usuario => !usuario.usuarioID || !usuario.usuarioNombre)) {
          throw new Error("La respuesta de operadores no contiene usuarioID y usuarioNombre.");
        }
        setOperadoresServicio({ asistenciaID: servicio, usuarios: data });
        cambiarOperador();
        if (data.length > 0) {
          form.setFieldsValue({ usuarioEmpleadoID: data[0].usuarioID });
        } else {
          form.resetFields(["usuarioEmpleadoID"]);
        }
      })
      .catch(error => {
        if (consulta === consultaServicioRef.current) setErrorContexto(error.message);
      })
      .finally(() => {
        if (consulta === consultaServicioRef.current) setCargandoContexto(false);
      });
  };

  useEffect(() => {
    setCaptura({ evaluacionID: null, rubros: [], respuestas: {} });
    setErrorRubros("");
    if (evaluacionID == null) {
      setCargandoRubros(false);
      return;
    }

    let vigente = true;
    setCargandoRubros(true);
    getListaRubros(evaluacionID)
      .then(data => {
        if (!vigente) return;
        const respuestas = Object.fromEntries(data.map(rubro => [
          rubro.id,
          { siCumple: null, oportunidad: "" },
        ]));
        setCaptura({ evaluacionID, rubros: data, respuestas });
      })
      .catch(error => {
        if (vigente) setErrorRubros(error.message);
      })
      .finally(() => {
        if (vigente) setCargandoRubros(false);
      });

    return () => {
      vigente = false;
    };
  }, [evaluacionID]);

  const responderRubro = (id, respuesta) => {
    setCaptura(actual => ({
      ...actual,
      respuestas: { ...actual.respuestas, [id]: respuesta },
    }));
  };

  const guardar = async values => {
    if (!formularioListo || !completo || guardandoRef.current) return;
    const body = {
      evaluacionID: values.evaluacionID,
      usuarioEmpleadoID: values.usuarioEmpleadoID,
      usuarioSupervisorID: values.usuarioSupervisorID,
      asistenciaID: values.asistenciaID.trim(),
      etiquetaLlamada: values.etiquetaLlamada.trim(),
      tipoMonitoreo: values.tipoMonitoreo,
      duracion: values.duracion.trim(),
      rubros: rubros.map(rubro => ({
        nombreRubro: rubro.nombreRubro,
        siCumple: respuestas[rubro.id].siCumple,
      })),
    };
    guardandoRef.current = true;
    setGuardando(true);
    try {
      const resultado = await nuevaEvaluacion(body);
      message.success(resultado);
      history.replace({
        pathname: "/evaluaciones",
        search: `?tipo=servicio&q=${encodeURIComponent(body.asistenciaID)}`,
      });
    } catch (error) {
      message.error(error.message);
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return {
    form,
    volver,
    operadores,
    servicioConsultado: operadoresServicio.asistenciaID === asistenciaID?.trim(),
    asistenciaID,
    cargandoInicial,
    errorInicial,
    operador,
    cargandoContexto,
    errorContexto,
    contextoListo,
    formularioListo,
    plantillas,
    supervisores,
    plantilla,
    rubros,
    cargandoRubros,
    errorRubros,
    respuestas,
    totalRubros,
    respondidos,
    completo,
    guardando,
    puedeGuardar: formularioListo && completo,
    initialValues: {
      fechaEvaluacion: hoy(),
      usuarioAnalistaNombre: user.nombre,
    },
    responderRubro,
    cambiarServicio,
    buscarServicio,
    cambiarOperador,
    guardar,
  };
}
